import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";

import { useCart } from "../../lib/CartContext";
import { useZeoContent } from "../../lib/ZeoContentContext";
import { optimizeImg } from "../../lib/img";

export default function CartItemsStep() {
  const { cart: items, increment, decrement, remove } = useCart();
  const { business } = useZeoContent();
  const navigate = useNavigate();

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="cart-empty">
        <div className="w-20 h-20 rounded-full bg-[#F8F8F8] flex items-center justify-center mb-6">
          <ShoppingBag className="w-8 h-8 text-[#888888]" />
        </div>
        <h1 className="font-serif text-2xl mb-3">Sepetiniz boş</h1>
        <p className="text-sm text-[#888888] max-w-sm mb-8">
          Premium çiçek koleksiyonumuza göz atın ve sevdiklerinize özel hediyeler keşfedin.
        </p>
        <Link
          to="/urunler"
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors"
        >
          Ürünleri Keşfet
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <h1 className="font-serif text-3xl mb-1">Sepetim</h1>
        <p className="text-sm text-[#888888] mb-8">{items.length} ürün</p>

        <div className="space-y-4" data-testid="cart-items">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex gap-5 p-5 bg-[#F8F8F8] rounded-2xl"
              data-testid={`cart-item-${item.id}`}
            >
              <img
                src={optimizeImg(item.image, 200)}
                alt={item.name}
                width="96"
                height="96"
                loading="lazy"
                decoding="async"
                className="w-24 h-24 rounded-xl object-cover shrink-0"
              />
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <h4 className="font-serif text-base font-medium text-[#222222] line-clamp-2 mb-1">
                    {item.name}
                  </h4>
                  <div className="text-[#A85C34] font-semibold">
                    {item.price.toLocaleString("tr-TR")}₺
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 bg-white rounded-full border border-[#EAEAEA] px-1">
                    <button
                      onClick={() => decrement(item.id)}
                      aria-label="Azalt"
                      className="w-8 h-8 flex items-center justify-center text-[#222222] hover:text-[#A85C34]"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-sm font-medium w-6 text-center">{item.qty}</span>
                    <button
                      onClick={() => increment(item.id)}
                      disabled={item.stock != null && item.stock !== -1 && item.qty >= item.stock}
                      aria-label="Artır"
                      className="w-8 h-8 flex items-center justify-center text-[#222222] hover:text-[#A85C34] disabled:opacity-30 disabled:hover:text-[#222222] disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => remove(item.id)}
                    aria-label="Kaldır"
                    className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white text-[#888888] hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {item.stock != null && item.stock !== -1 && item.qty >= item.stock && (
                  <p className="text-[11px] text-amber-600 mt-1">Stokta {item.stock} adet var</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-8 rounded-2xl border border-[#EAEAEA] p-6 space-y-4" data-testid="cart-summary">
          <h2 className="font-serif text-lg">Sipariş Özeti</h2>
          <div className="flex items-baseline justify-between border-t border-[#EAEAEA] pt-4">
            <span className="text-[#555555]">Ara Toplam</span>
            <span className="font-serif text-2xl font-bold text-[#222222]" data-testid="cart-subtotal">
              {subtotal.toLocaleString("tr-TR")}₺
            </span>
          </div>
          <button
            onClick={() => navigate("/sepet/teslimat")}
            className="w-full flex items-center justify-center gap-2 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-4 font-medium transition-colors"
            data-testid="cart-checkout-order"
          >
            Teslimat Bilgilerine Geç
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href={`https://wa.me/${business.whatsapp.replace("+", "")}?text=${encodeURIComponent(
              "Merhaba, sipariş vermek istiyorum:\n\n" +
                items.map((i) => `• ${i.name} x${i.qty} = ${(i.price * i.qty).toLocaleString("tr-TR")}₺`).join("\n") +
                `\n\nToplam: ${subtotal.toLocaleString("tr-TR")}₺`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center border border-[#222222] text-[#222222] hover:bg-[#222222] hover:text-white rounded-full px-6 py-3.5 font-medium transition-colors"
            data-testid="cart-checkout-whatsapp"
          >
            WhatsApp ile Sipariş Ver
          </a>
          <p className="text-xs text-[#888888] text-center">
            veya {business.phone} numaramızdan bizi arayın
          </p>
        </div>
      </div>
    </div>
  );
}
